import { useCallback, useMemo } from 'react';
import Helmet from 'react-helmet';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import { useLocation } from 'react-router-dom';
import { useRouter } from '~/utils/navigation';
import Icon, { ICON_SIZE_FULLSCREEN_LINE, ICON_SIZE_MINIMIZE_LINE, ICON_MENU } from '~/components/icon/index.js';
import breakpoint, { getZindexByType } from '~/utils/style-utils.ts';
import IconButtonClose from '~/components/misc/icon-button-close.js';
import { toggleLoveGame } from '~/components/casino-shared/ducks/index';
import { showNotification } from '~/components/notifications/ducks/notifications.js';
import LoveButton from '~/components/casino-shared/love-button.js';
import useRouterCallbacks from '~/hooks/use-router-callbacks';
import JurisdictionLogo from './jurisdiction-logo.js';
import { isBiggerThanLargeSelector, isSmallerSelector } from '~/components/layout/mediaquery.js';
import { currentGameSelector } from '~/components/casino-shared/game-loader/ducks/selectors';
import { cleanUp } from '~/components/casino-shared/game-loader/ducks/actions';
import { ROUTE_GLOBAL } from '~/components/router/constants.js';
import useCasinoRoutes from '~/components/router/use-casino-routes';
import useLoginFlow from '~/hooks/use-login-flow.js';
import { lovedGamesSelector } from '~/components/casino-shared/ducks/selectors';
import useTranslations from '~/components/translation/use-translations.ts';
import { authenticationSelector } from '~/components/authentication/ducks/selectors.js';
import { useFullScreen } from '~/hooks/use-fullscreen';
import { isFullscreenSupported } from '~/utils/fullscreen';
import { reportException } from '@comeon/error-reporter/src/error-reporter';
import { ErrorReporterDomain } from '~/components/error-handling/error-reporter-domain';

const Nav = styled.div`
	height: var(--smallerHeaderHeight);
	padding: 5px 12px 6px 12px;
	z-index: ${getZindexByType('header')};
	.svg-logo {
		height: 40px;
	}
	${breakpoint.lg`
		height: var(--headerHeight);
		padding: 12px 24px;
	`};
`;

const Burger = styled.button`
	border: none;
	background-color: transparent;
	width: 24px;
	&:focus {
		outline: none;
	}
`;

const Wrapper = styled.header`
	height: 100%;
	display: flex;
	position: relative;
`;

const LeftSide = styled.div`
	display: flex;
	> div:first-child {
		padding-left: 0;
	}
`;

export const IconStyled = styled.div`
	padding: 0 12px;
	display: flex;
	align-items: center;
	svg {
		fill: var(--ingame-nav-icons, rgba(var(--base-02), 1));
	}
`;

const Title = styled.h2`
	align-self: center;
	border-left: 2px solid var(--ingame-border-left-line, rgba(var(--base-11), 0.5));
	color: var(--ingame-nav-title, rgba(var(--base-02), 1));
	font: var(--h4-bold);
	padding: 6px 0 4px 10px;
`;

const textKeys = ['CASINO_GAMES_TITLE_TEMPLATE', 'CASINO_GAMES_DESCRIPTION', 'LOVEGAMES_LOGOUT'];

const preventEvent = e => {
	if (e) {
		e.preventDefault();
		e.stopPropagation();
	}
};

const IngameDesktopHeader = ({ RightContainerHeader, isInGame }) => {
	const dispatch = useDispatch();
	const {
		translations: { LOVEGAMES_LOGOUT, CASINO_GAMES_TITLE_TEMPLATE, CASINO_GAMES_DESCRIPTION }
	} = useTranslations(textKeys);
	const currentGame = useSelector(currentGameSelector);
	const isSmaller = useSelector(isSmallerSelector);
	const isBiggerThanLarge = useSelector(isBiggerThanLargeSelector);
	const authenticated = useSelector(authenticationSelector);
	const lovedGames = useSelector(lovedGamesSelector);
	const { initializeLogin } = useLoginFlow();
	const { transformPathAndReplace } = useRouterCallbacks();
	const router = useRouter();
	const location = useLocation();
	const { ROUTE_CASINO_EXPLORE, ROUTE_LIVE_CASINO_EXPLORE } = useCasinoRoutes();
	const fullscreen = useFullScreen();

	const onBurgerClick = e => {
		preventEvent(e);
		transformPathAndReplace(ROUTE_GLOBAL);
	};

	const onLoveButtonClick = useCallback(
		e => {
			preventEvent(e);

			if (!authenticated) {
				initializeLogin({
					from: location.pathname
				});
				dispatch(
					showNotification({
						message: LOVEGAMES_LOGOUT,
						color: 'neutral',
						autoHide: true
					})
				);
				return;
			}

			if (currentGame) {
				dispatch(toggleLoveGame(currentGame.code));
			}
		},
		[dispatch, initializeLogin, location.pathname, currentGame, authenticated, LOVEGAMES_LOGOUT]
	);

	const toggleFullscreen = async () => {
		try {
			if (fullscreen.active) {
				return await fullscreen.exit();
			}
			return await fullscreen.enter();
		} catch (error) {
			reportException(error, {
				domain: ErrorReporterDomain.CASINO
			});
		}
	};

	const getReferrer = useCallback(() => {
		let basePathname = '';
		if (currentGame) {
			if (currentGame.categoryCode === 'CASINO_LIVE_CASINO') {
				basePathname = ROUTE_LIVE_CASINO_EXPLORE;
			} else {
				basePathname = ROUTE_CASINO_EXPLORE;
			}
		}
		return basePathname;
	}, [ROUTE_CASINO_EXPLORE, ROUTE_LIVE_CASINO_EXPLORE, currentGame]);

	const handleGoBack = useCallback(() => {
		dispatch(cleanUp());
		router.push(getReferrer());
	}, [dispatch, getReferrer, router]);

	const metaDescription = useMemo(() => {
		if (currentGame && CASINO_GAMES_DESCRIPTION) {
			return CASINO_GAMES_DESCRIPTION.replace(/%s/g, currentGame.name);
		}
		return '';
	}, [currentGame, CASINO_GAMES_DESCRIPTION]);

	return (
		<Nav>
			<Helmet titleTemplate={CASINO_GAMES_TITLE_TEMPLATE}>
				{currentGame && <title>{currentGame?.name}</title>}
				{metaDescription && <meta name="description" content={metaDescription} />}
			</Helmet>
			<Wrapper>
				<LeftSide className="ingame-desktop-header__left-side">
					{isSmaller && (
						<Burger onClick={onBurgerClick}>
							<Icon type={ICON_MENU} />
						</Burger>
					)}
					<IconStyled>
						<IconButtonClose onClick={handleGoBack} dataAt="in-game-close-button" />
					</IconStyled>
					{isFullscreenSupported() && (
						<IconStyled>
							<button type="button" onClick={toggleFullscreen} data-at="in-game-fullscreen-button">
								{fullscreen.active ? (
									<Icon type={ICON_SIZE_MINIMIZE_LINE} />
								) : (
									<Icon type={ICON_SIZE_FULLSCREEN_LINE} />
								)}
							</button>
						</IconStyled>
					)}
					<IconStyled>
						<LoveButton
							loved={lovedGames.includes(currentGame?.code)}
							onClick={onLoveButtonClick}
							showCount={false}
						/>
					</IconStyled>
					<JurisdictionLogo />
				</LeftSide>
				{isBiggerThanLarge && <Title data-at="in-game-title">{currentGame?.name}</Title>}
				<RightContainerHeader isInGame={isInGame} />
			</Wrapper>
		</Nav>
	);
};

export default IngameDesktopHeader;
